/** Minimum supported raster scale — below this, text quality is unacceptable. */
export const PROPOSAL_PDF_EXPORT_MIN_SCALE = 1.5;

const BYTES_PER_CANVAS_PIXEL = 4;

/** Safari: conservative peak canvas memory before auto scale-down. */
export const PROPOSAL_PDF_SAFARI_SAFE_PEAK_BYTES = 96 * 1024 * 1024;

/** Non-Safari peak canvas memory guidance before auto scale-down. */
export const PROPOSAL_PDF_DEFAULT_SAFE_PEAK_BYTES = 180 * 1024 * 1024;

/** Hard stop — single-page canvas at minimum scale. */
export const PROPOSAL_PDF_HARD_PEAK_BYTES = 140 * 1024 * 1024;

/** Hard stop — cumulative raster payload retained by jsPDF until save(). */
export const PROPOSAL_PDF_HARD_TOTAL_BYTES = 360 * 1024 * 1024;

export const PROPOSAL_PDF_TOO_LARGE_MESSAGE =
  "This proposal is too large to generate reliably in this browser. Please try Chrome or contact an administrator.";

export type ProposalPdfPageDimensions = {
  width: number;
  height: number;
};

export type ProposalPdfMemoryEstimate = {
  peakCanvasBytes: number;
  totalImageBytes: number;
};

export type ProposalPdfExportScalePlan = {
  scale: number;
  baseScale: number;
  optimizing: boolean;
  hardLimitExceeded: boolean;
  memory: ProposalPdfMemoryEstimate;
};

export function isSafariBrowser(userAgent = typeof navigator !== "undefined" ? navigator.userAgent : ""): boolean {
  return /Safari/i.test(userAgent) && !/Chrome|Chromium|Edg|OPR|OPiOS|CriOS|FxiOS/i.test(userAgent);
}

export function detectProposalPdfExportBrowser(userAgent = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  if (isSafariBrowser(userAgent)) return "safari";
  if (/Edg\//i.test(userAgent)) return "edge";
  if (/Chrome|Chromium/i.test(userAgent)) return "chrome";
  if (/Firefox/i.test(userAgent)) return "firefox";
  return "other";
}

/** Page-count policy — upper bound before memory planner may reduce further. */
export function resolveBaseScaleForPageCount(pageCount: number, devicePixelRatio = 1): number {
  if (pageCount <= 15) {
    return Math.min(3, Math.max(2, Math.round(devicePixelRatio)));
  }
  if (pageCount <= 30) {
    return 2;
  }
  return pageCount >= 50 ? PROPOSAL_PDF_EXPORT_MIN_SCALE : 2;
}

export function estimateCanvasBytes(width: number, height: number, scale: number): number {
  const w = Math.max(1, Math.ceil(width * scale));
  const h = Math.max(1, Math.ceil(height * scale));
  return w * h * BYTES_PER_CANVAS_PIXEL;
}

export function estimateProposalPdfExportMemory(
  pages: ProposalPdfPageDimensions[],
  scale: number,
): ProposalPdfMemoryEstimate {
  let peakCanvasBytes = 0;
  let totalImageBytes = 0;
  for (const page of pages) {
    const bytes = estimateCanvasBytes(page.width, page.height, scale);
    peakCanvasBytes = Math.max(peakCanvasBytes, bytes);
    totalImageBytes += bytes;
  }
  return { peakCanvasBytes, totalImageBytes };
}

export function measureProposalPdfPageDimensions(page: HTMLElement): ProposalPdfPageDimensions {
  const rect = page.getBoundingClientRect();
  return {
    width: Math.max(1, Math.round(rect.width)),
    height: Math.max(1, Math.round(rect.height)),
  };
}

function isWithinSoftLimits(
  memory: ProposalPdfMemoryEstimate,
  peakLimit: number,
  totalLimit: number,
): boolean {
  return memory.peakCanvasBytes <= peakLimit && memory.totalImageBytes <= totalLimit;
}

function isHardLimitExceeded(memory: ProposalPdfMemoryEstimate): boolean {
  return (
    memory.peakCanvasBytes > PROPOSAL_PDF_HARD_PEAK_BYTES ||
    memory.totalImageBytes > PROPOSAL_PDF_HARD_TOTAL_BYTES
  );
}

/**
 * Choose export scale from page count + memory estimate.
 * Reduces scale on Safari/large exports before capture; hard-fails if still unsafe at min scale.
 */
export function planProposalPdfExportScale(input: {
  pages: ProposalPdfPageDimensions[];
  devicePixelRatio?: number;
  isSafari?: boolean;
  peakSafeBytes?: number;
  totalSafeBytes?: number;
}): ProposalPdfExportScalePlan {
  const pageCount = input.pages.length;
  const baseScale = resolveBaseScaleForPageCount(pageCount, input.devicePixelRatio ?? 1);
  const isSafari = input.isSafari ?? isSafariBrowser();
  const peakSafeBytes = input.peakSafeBytes ?? (isSafari ? PROPOSAL_PDF_SAFARI_SAFE_PEAK_BYTES : PROPOSAL_PDF_DEFAULT_SAFE_PEAK_BYTES);
  const totalSafeBytes = input.totalSafeBytes ?? (isSafari ? PROPOSAL_PDF_HARD_TOTAL_BYTES * 0.55 : PROPOSAL_PDF_HARD_TOTAL_BYTES * 0.75);

  const scaleCandidates = [...new Set([baseScale, 2, PROPOSAL_PDF_EXPORT_MIN_SCALE])]
    .filter((scale) => scale >= PROPOSAL_PDF_EXPORT_MIN_SCALE)
    .sort((a, b) => b - a);

  for (const scale of scaleCandidates) {
    const memory = estimateProposalPdfExportMemory(input.pages, scale);
    if (isWithinSoftLimits(memory, peakSafeBytes, totalSafeBytes)) {
      return {
        scale,
        baseScale,
        optimizing: scale < baseScale,
        hardLimitExceeded: false,
        memory,
      };
    }
  }

  let scale = baseScale;
  let optimizing = false;
  while (scale > PROPOSAL_PDF_EXPORT_MIN_SCALE + 0.001) {
    const memory = estimateProposalPdfExportMemory(input.pages, scale);
    if (isWithinSoftLimits(memory, peakSafeBytes, totalSafeBytes)) {
      return { scale, baseScale, optimizing, hardLimitExceeded: false, memory };
    }
    scale = Math.max(PROPOSAL_PDF_EXPORT_MIN_SCALE, Math.round((scale - 0.5) * 2) / 2);
    optimizing = true;
  }

  const minMemory = estimateProposalPdfExportMemory(input.pages, PROPOSAL_PDF_EXPORT_MIN_SCALE);
  return {
    scale: PROPOSAL_PDF_EXPORT_MIN_SCALE,
    baseScale,
    optimizing: true,
    hardLimitExceeded: isHardLimitExceeded(minMemory),
    memory: minMemory,
  };
}
